/**
 * LeaderboardPage - PS Leaderboard with 2 ranking categories
 * Handicap and Golf Coins
 * Features: Auto-scroll to current user, state/course filters
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { handicapRanking, verifiedIndex, leaderboardHandicapLabel } from '../utils/handicap';
import { 
  Trophy, 
  ArrowLeft, 
  Medal,
  MapPin,
  Filter,
  ChevronDown,
  X
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to extract state from location string
// Handles: "Denver, CO" -> "CO", "Brooklyn, NY" -> "NY", "Michigan" -> "MI", etc.
const extractState = (location) => {
  if (!location) return null;
  
  // Common state abbreviation mappings for full names
  const stateMap = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
    'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
    'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
    'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
    'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
    'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
    'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
    'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
    'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
    'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
    'wisconsin': 'WI', 'wyoming': 'WY'
  };
  
  // If location contains comma, extract state from "City, STATE" format
  if (location.includes(',')) {
    const parts = location.split(',');
    const state = parts[parts.length - 1].trim().toUpperCase();
    // Return if it's a 2-letter abbreviation
    if (state.length === 2) return state;
  }
  
  // Check if the whole location is a state name
  const lowerLoc = location.toLowerCase().trim();
  if (stateMap[lowerLoc]) return stateMap[lowerLoc];
  
  // If it's already a 2-letter code
  if (location.trim().length === 2) return location.trim().toUpperCase();
  
  return null;
};

const LeaderboardPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('handicap');
  const [golfers, setGolfers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [stateFilter, setStateFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  
  // Refs for auto-scroll
  const currentUserRef = useRef(null);
  const listContainerRef = useRef(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    fetchGolfers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Auto-scroll to current user when data loads or tab changes
  useEffect(() => {
    if (!loading && currentUserRef.current && !hasScrolledRef.current) {
      setTimeout(() => {
        currentUserRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        hasScrolledRef.current = true;
      }, 300);
    }
  }, [loading, activeTab]);

  // Reset scroll flag when tab changes so we re-scroll
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [activeTab]);

  const fetchGolfers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/golfers/discover?limit=500`);
      // API returns { golfers: [...], total: X, ... } - extract the array
      const data = response.data;
      let golfersList = Array.isArray(data) ? data : (data?.golfers || []);
      
      // The discover endpoint excludes the current user, but for the leaderboard 
      // we need to include them. Inject the current user's profile into the list.
      if (user?.user_id) {
        const currentUserExists = golfersList.some(g => g.user_id === user.user_id);
        if (!currentUserExists) {
          // Fetch current user's full profile from /api/profile/me (has handicap)
          // AND /api/golf-coins/balance (has coin balance)
          try {
            const [profileRes, coinsRes] = await Promise.all([
              axios.get(`${API}/api/profile/me`),
              axios.get(`${API}/api/golf-coins/balance`).catch(() => ({ data: { balance: 0 } }))
            ]);
            
            const myProfile = profileRes.data;
            const myCoins = coinsRes.data;
            
            golfersList = [
              {
                user_id: myProfile.user_id,
                name: myProfile.name,
                username: myProfile.username,
                picture: myProfile.picture,
                handicap: myProfile.handicap,
                handicap_verified: myProfile.handicap_verified,
                handicap_visibility: myProfile.handicap_visibility,
                golf_coins: myCoins.balance || 0,
                tournament_wins: myProfile.tournament_wins || 0,
                location: myProfile.location,
                city: myProfile.city,
                home_course: myProfile.home_course,
                is_online: true, // Current user is always online
                is_boosted: false
              },
              ...golfersList
            ];
          } catch (profileErr) {
            console.error("Error fetching current user profile:", profileErr);
            // Fallback: use the user prop data
            golfersList = [
              {
                user_id: user.user_id,
                name: user.name,
                username: user.username,
                picture: user.picture,
              handicap: null,
              handicap_verified: false,
                golf_coins: user.golf_coins || 0,
                tournament_wins: user.tournament_wins || 0,
                location: user.location,
                city: user.city,
                home_course: user.home_course,
                is_online: true,
                is_boosted: false
              },
              ...golfersList
            ];
          }
        }
      }
      
      setGolfers(golfersList);
    } catch (error) {
      console.error("Error fetching golfers:", error);
      toast.error("Failed to load leaderboard");
      setGolfers([]); // Ensure empty array on error
    } finally {
      setLoading(false);
    }
  };
  
  // Extract unique states for filter dropdown - memoized for performance
  const uniqueStates = useMemo(() => {
    const states = golfers
      .map(g => extractState(g.location || g.city))
      .filter(Boolean);
    return [...new Set(states)].sort();
  }, [golfers]);
  
  const uniqueCourses = useMemo(() => 
    [...new Set(
      golfers
        .map(g => g.home_course)
        .filter(Boolean)
    )].sort()
  , [golfers]);

  const getRankBadge = (rank) => {
    if (rank === 1) return { emoji: '🥇', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (rank === 2) return { emoji: '🥈', color: 'text-gray-300', bg: 'bg-gray-500/20' };
    if (rank === 3) return { emoji: '🥉', color: 'text-amber-600', bg: 'bg-amber-500/20' };
    return { emoji: null, color: 'text-gray-400', bg: 'bg-gray-800/50' };
  };

  // Apply state and course filters
  const applyFilters = useCallback((list) => {
    let filtered = [...list];
    
    if (stateFilter !== 'all') {
      filtered = filtered.filter(g => {
        const golferState = extractState(g.location || g.city);
        return golferState === stateFilter;
      });
    }
    
    if (courseFilter !== 'all') {
      filtered = filtered.filter(g => g.home_course === courseFilter);
    }
    
    return filtered;
  }, [stateFilter, courseFilter]);

  // Get ranked data based on active tab - ALL users included, no handicaps at bottom
  const getRankedData = useCallback(() => {
    // Ensure golfers is always an array
    if (!Array.isArray(golfers) || golfers.length === 0) return [];
    
    // Apply state/course filters first
    const filteredGolfers = applyFilters(golfers);
    
    switch(activeTab) {
      case 'handicap': {
        // Separate users WITH handicaps and WITHOUT handicaps
        const withHandicap = filteredGolfers.filter(verifiedIndex);
        const withoutHandicap = filteredGolfers.filter(g => !verifiedIndex(g));
        
        // Sort those with handicaps (lower is better)
        withHandicap.sort((a, b) => a.handicap - b.handicap);
        
        // Combine: with handicaps first (sorted), then without handicaps at the bottom
        const combinedHandicap = handicapRanking([...withHandicap, ...withoutHandicap]);
        
        return combinedHandicap.map(g => ({ 
          ...g, 
          value: leaderboardHandicapLabel(g),
          label: 'HCP',
          hasValue: verifiedIndex(g)
        }));
      }
        
      case 'coins': {
        // Separate users WITH coins and WITHOUT coins
        const withCoins = filteredGolfers.filter(g => g.golf_coins != null && g.golf_coins > 0);
        const withoutCoins = filteredGolfers.filter(g => !g.golf_coins || g.golf_coins === 0);
        
        // Sort by most coins (higher is better)
        withCoins.sort((a, b) => (b.golf_coins || 0) - (a.golf_coins || 0));
        
        // Combine: with coins first (sorted), then without at the bottom
        const combinedCoins = [...withCoins, ...withoutCoins];
        
        return combinedCoins.map(g => ({ 
          ...g, 
          value: g.golf_coins || 0, 
          label: 'Coins',
          hasValue: g.golf_coins != null && g.golf_coins > 0
        }));
      }
        
      default:
        return [];
    }
  }, [golfers, activeTab, applyFilters]);
  
  const clearFilters = () => {
    setStateFilter('all');
    setCourseFilter('all');
  };
  
  const hasActiveFilters = stateFilter !== 'all' || courseFilter !== 'all';

  const rankedData = loading ? [] : getRankedData();
  const rankedCount = activeTab === 'handicap' ? rankedData.filter(g => g.handicap_rank != null).length : rankedData.length;

  const tabs = [
    { id: 'handicap', label: 'Handicap', icon: '⛳', desc: 'Lowest handicaps' },
    { id: 'coins', label: 'Coins', icon: '🪙', desc: 'Most golf coins earned' }
  ];

  if (loading) {
    return (
      <div className="ps-theme-page min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="ps-theme-page min-h-screen" data-testid="leaderboard-page">
      {/* Header */}
      <div className="ps-header-bar sticky top-0 z-40 backdrop-blur-sm border-b" data-testid="leaderboard-theme-header">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/home')}
              className="p-2 rounded-full bg-gray-800 text-gray-400 hover:text-white transition-colors"
              data-testid="leaderboard-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-400" />
                PS Leaderboard
              </h1>
              <p data-testid="leaderboard-rank-summary" className="text-xs text-gray-400">
                {rankedCount} ranked{rankedData.length > rankedCount ? ` · ${rankedData.length - rankedCount} unranked` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Filter Toggle Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-full transition-colors ${
                hasActiveFilters 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-5 h-5" />
            </button>
            {rankedCount >= 1 && <span data-testid="leaderboard-podium-first" className="text-2xl">🥇</span>}
            {rankedCount >= 2 && <span data-testid="leaderboard-podium-second" className="text-xl">🥈</span>}
            {rankedCount >= 3 && <span data-testid="leaderboard-podium-third" className="text-lg">🥉</span>}
          </div>
        </div>
        
        {/* Filter Panel */}
        {showFilters && (
          <div className="px-4 pb-3 space-y-3 border-t border-gray-800 pt-3 bg-gray-900/50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400 font-medium">Filter Rankings</span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-3 h-3" />
                  Clear filters
                </button>
              )}
            </div>
            
            {/* State Filter */}
            <div className="relative">
              <label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                State
              </label>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-emerald-500"
                data-testid="filter-state"
              >
                <option value="all">All States</option>
                {uniqueStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-7 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            
            {/* Golf Course Filter */}
            <div className="relative">
              <label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <span>⛳</span>
                Golf Course
              </label>
              <select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-emerald-500"
                data-testid="filter-course"
              >
                <option value="all">All Courses</option>
                {uniqueCourses.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-7 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}
        
        {/* Tab Navigation */}
        <div className="flex px-4 pb-3 gap-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === tab.id 
                  ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-gray-900' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <span className="mr-1.5">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Description Banner */}
      <div className="px-4 py-3 bg-gradient-to-r from-yellow-900/20 to-amber-900/20 border-b border-yellow-500/10">
        <p className="text-yellow-400/80 text-sm text-center font-medium">
          {tabs.find(t => t.id === activeTab)?.desc}
        </p>
      </div>
      
      {/* Rankings List */}
      <div className="p-4 pb-24 space-y-2" ref={listContainerRef}>
        {rankedData.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-10 h-10 text-gray-600" />
            </div>
            <p className="text-gray-400 text-lg font-medium mb-2">
              {hasActiveFilters ? 'No matches found' : 'No rankings yet'}
            </p>
            <p className="text-gray-500 text-sm max-w-xs mx-auto">
              {hasActiveFilters 
                ? 'Try adjusting your filters to see more golfers.'
                : activeTab === 'coins'
                ? 'Earn golf coins by playing rounds and competing!'
                : 'Play rounds and log scores to get ranked!'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                data-testid="leaderboard-empty-clear-filters"
                className="mt-4 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm hover:bg-emerald-500/30 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          rankedData.map((golfer, index) => {
            const rank = activeTab === 'handicap' ? golfer.handicap_rank : index + 1;
            const badge = getRankBadge(rank);
            const initials = golfer.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'G';
            const isCurrentUser = golfer.user_id === user?.user_id;
            // Extract state from location for display
            const golferState = extractState(golfer.location || golfer.city);
            
            return (
              <div
                key={golfer.user_id}
                ref={isCurrentUser ? currentUserRef : null}
                onClick={() => navigate(`/discover`)}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:scale-[1.01] ${
                  isCurrentUser 
                    ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-2 border-yellow-500 ring-2 ring-yellow-500/30 shadow-lg shadow-yellow-500/20' 
                    : rank != null && rank <= 3
                    ? badge.bg + ' border border-gray-700/50' 
                    : 'bg-gray-800/40 hover:bg-gray-800/60'
                }`}
                data-testid={rank == null ? `leaderboard-unranked-${golfer.user_id}` : `leaderboard-rank-${rank}`}
              >
                {/* Rank */}
                <div className={`w-11 h-11 flex items-center justify-center rounded-full shrink-0 ${
                  rank != null && rank <= 3 ? 'text-2xl' : 'bg-gray-700 text-gray-300 text-xs font-bold'
                }`}>
                  {rank == null ? 'N/A' : badge.emoji || rank}
                </div>
                
                {/* Avatar */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden shrink-0 ${
                  isCurrentUser 
                    ? 'bg-yellow-500/30 border-2 border-yellow-500' 
                    : 'bg-gradient-to-br from-gray-700 to-gray-800 border-2 border-gray-600'
                }`}>
                  {golfer.picture ? (
                    <img src={golfer.picture} alt={golfer.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className={`font-bold ${isCurrentUser ? 'text-yellow-400' : 'text-gray-400'}`}>{initials}</span>
                  )}
                </div>
                
                {/* Name & State */}
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold truncate ${
                    isCurrentUser ? 'text-yellow-400' : rank != null && rank <= 3 ? badge.color : 'text-white'
                  }`}>
                    {golfer.name} {isCurrentUser && <span className="text-yellow-300 text-xs font-bold ml-1">(You)</span>}
                  </p>
                  <p className="text-gray-500 text-xs truncate flex items-center gap-1">
                    {golferState && <MapPin className="w-3 h-3" />}
                    {golferState || golfer.home_course || 'Par Seeker Member'}
                  </p>
                </div>
                
                {/* Value */}
                <div className="text-right shrink-0">
                  <p data-testid={`leaderboard-value-${golfer.user_id}`} className={`font-bold ${golfer.hasValue ? 'text-xl' : 'text-xs max-w-24 whitespace-normal'} ${
                    !golfer.hasValue 
                      ? 'text-gray-600' 
                      : activeTab === 'coins' 
                      ? 'text-amber-400' 
                      : 'text-emerald-400'
                  }`}>
                    {activeTab === 'coins' && golfer.hasValue && <span className="text-sm mr-0.5">🪙</span>}
                    {golfer.value}
                  </p>
                  <p className="text-gray-500 text-xs">{golfer.label}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Footer */}
      <div className="ps-footer-fade fixed bottom-0 left-0 right-0 p-4">
        <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
          <Medal className="w-4 h-4" />
          <span>Rankings update in real-time</span>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
